import React, {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState
} from "react";
import { useSearchTerm } from "utilities/search";
import { OperationContext } from "@urql/core";
import { DeviceCardProps, getSoftwareVersion } from "./DeviceCard";
import { FilterActionFcn } from "collections/FilterPanel";
import {
  mapFieldToGraphQL,
  verifyAndFormatAggregates
} from "utilities/filters/deviceFilters";
import i18n, { useTranslation } from "utilities/i18n";
import {
  DeviceIn,
  DevicesCardPropsFragment,
  DevicesLocationQuery,
  EntityConnectionEdge,
  useActiveEntitlementsBySerialQuery,
  useLinkedDevicesForDeviceQuery,
  useProvisioningAccountsQuery
} from "generated/graphql";
import { noOpNotifyContext } from "polygraphql";
import { Layout } from "views/Main/Layouts";
import ViewSelector from "views/ViewSelector/ViewSelector";
import {
  areFiltersSelected,
  ConnectionStatusIcon,
  DeviceFilterState,
  DropdownButton,
  DropdownList,
  FeatureFlagEnabled,
  LoadingIndicator,
  NoResultsIndicator,
  OptionType,
  PolyIcon,
  ToolTip,
  useDeviceFilterValue
} from "elements";
import { useDevices } from "./useDevices";
import { useDeviceCount } from "./useDeviceCount";
import { useDispatch, useSelector } from "react-redux";
import { useRouteMatch } from "react-router";
import { State } from "store";
import { clearGeofilter } from "store/devicesMap";
import { infiniteNestLink, Link, Route } from "polyrouter";
import { NoDevicesIndicator } from "./NoDevicesIndicator";
import {
  CheckTableData,
  CheckTableHeader,
  ClearFiltersHeader,
  getColumns,
  HEADER_ID,
  itemCheckedWithSet,
  TableBody,
  TableColumn,
  TableHeader,
  TableHeaderItem,
  TableRow,
  TableSearchEdit,
  toggleAll,
  toggleItemWithSet
} from "collections/Table";
import {
  DeleteBulkDevicesModal,
  DeviceInfo
} from "routes/Manage/Inventory/Delete/DeleteDeviceModal";
import { TABLE_COLUMN_STATE, TABLES } from "store/userMeta";
import { SortState } from "store/filters";
import {
  isPolyOS,
  deviceHasExtraOptions,
  isUcsDevice
} from "utilities/modelSelectors";
import {
  getProvisioningConnectionInfo,
  getProvisioningState,
  getStatusString,
  isProvisionedDevice,
  ProvisioningState
} from "elements/ConnectionStatusIndicator/ConnectionStatusIndicator";
import { handleNullFields } from "utilities/stringUtils";
import VisibilitySensor from "react-visibility-sensor";
import { Guard, ProtectedLink, ProtectedRoute } from "TsRoleGuards";
import { needs, useGuard } from "rbac";
import { formatDate } from "../View/DeviceDetails";
import { useLineQueryMapped } from "../View/Diagnostics/ServiceInformationTable";
import CreateDeviceGroupModal from "routes/Manage/DeviceGroups/Create/CreateDeviceGroupModal";
import { AddToExistingGroupModal } from "../View/GroupMembership/GroupMembership";
import { NestedLinesTable } from "./NestedLinesTable";
import { DESKTOP_PROXY_AGENT } from "Constants";
import { SelectedTenantContext } from "polyrouter/SelectedTenantProvider";
import { DialOutModal } from "../DialOut/DialOutModal";
import {
  useEntityConnections,
  getLinkedDevicesStatus
} from "../View/useEntityConnections";
import { DeviceInventoryExport } from "./DeviceExport";

export type DeviceRowProps = DevicesCardPropsFragment & {
  accountProvisioningDisabled: boolean;
  checked: boolean;
  connectedDevices?: [EntityConnectionEdge];
  connectionStatus: boolean | null;
  count: number;
  index: number;
  listLength: number;
  onCheck: (id: string) => void;
  setCursor: Dispatch<SetStateAction<number>>;
  showChecks: boolean;
  shownColumns: Array<string>;
  policyView?: boolean;
  linkedDevicesTooltipHidden?: boolean;
  hideLinkedDevicesTooltip?: (hideTooltip: boolean) => void;
};

interface ConnectionStatusProps<T> {
  fieldName: "connected" | "status";
  filterState: T;
  setFilter: (filters: T) => void;
}

export function ExpandCellVisibility({
  id,
  onChange
}: {
  id: string;
  onChange: (open: boolean) => void;
}) {
  return (
    <VisibilitySensor>
      {({ isVisible }) => (
        <ExpandoCell id={id} onChange={onChange} isVisible={isVisible} />
      )}
    </VisibilitySensor>
  );
}

function ExpandoCell({
  id,
  isVisible,
  onChange
}: {
  id: string;
  isVisible: boolean;
  onChange: (open: boolean) => void;
}) {
  const [expanded, setExpand] = useState(false);
  const [pause, setPaused] = useState(true);
  const { lines, operation, fetching } = useLineQueryMapped(id, {
    pause
  });
  const [hovered, setHovered] = useState(false);
  useEffect(() => {
    onChange(expanded);
  }, [expanded, onChange]);
  useEffect(() => {
    if (isVisible) {
      setPaused(false);
    }
  }, [isVisible, setPaused]);
  const isPureCacheRequest = operation?.context.requestPolicy === "cache-only";
  useEffect(() => {
    //If we did not pull from the cache, and we don't have data close out our ability to use the expand.
    if (lines === undefined && !isPureCacheRequest) {
      setExpand(false);
    }
  }, [lines, isPureCacheRequest]);
  const hasLinesToShow =
    (lines === undefined && isPureCacheRequest) || (lines?.length ?? 0) > 0;
  const onClick = useCallback(() => {
    hasLinesToShow && setExpand(e => !e);
  }, [hasLinesToShow, setExpand]);
  const onMouseEvent = () => setHovered(!hovered);
  return (
    <>
      {fetching ? (
        <PolyIcon icon="refresh" className="spin" />
      ) : hasLinesToShow ? (
        <button className={"btn btn-icon"} onClick={onClick}>
          <PolyIcon
            icon={hovered || expanded ? "phone_fill" : "phone_outline"}
            onMouseEnter={onMouseEvent}
            onMouseLeave={onMouseEvent}
          />
        </button>
      ) : (
        <div>{handleNullFields(null, true)}</div>
      )}
    </>
  );
}

export const generateTableHeaders = (
  shown: Array<TableColumn>,
  filterState: DeviceFilterState,
  updateFilter: (newFilterState: DeviceFilterState) => void,
  filterOptions: { [p: string]: string[] }
) => {
  const shownColumns = shown.map(column =>
    getTableHeaders(
      column.key,
      column.title,
      filterState,
      updateFilter,
      filterOptions
    )
  );

  if (
    shownColumns.length &&
    shownColumns.length <= 2 &&
    (shownColumns[0].key === TABLES.DEVICES.COLUMNS.PROVISIONING ||
      shownColumns[0].key === TABLES.DEVICES.COLUMNS.CONNECTION)
  ) {
    return [...shownColumns, <th></th>];
  } else if (shownColumns.length) {
    return shownColumns;
  } else {
    return [<th></th>];
  }
};

export const getTableHeaders = (
  column: string,
  title: string,
  filterState: DeviceFilterState,
  updateFilter: (newFilterState: DeviceFilterState) => void,
  filterOptions: { [key: string]: string[] }
) => {
  const { COLUMNS, HIDDEN } = TABLES.DEVICES;

  const ALL_COLUMNS = { ...COLUMNS, ...HIDDEN };
  switch (column) {
    case ALL_COLUMNS.CONNECTION:
      return (
        <ConnectionStatusHeader
          fieldName="connected"
          filterState={filterState}
          setFilter={updateFilter}
          key={column}
        />
      );
    case ALL_COLUMNS.PROVISIONING:
      const provisionedOptions: Array<OptionType> = [
        {
          value: ProvisioningState.INACTIVE,
          text: i18n.t("PROVISIONED"),
          icon: <PolyIcon icon="server" className="v-middle" />
        },
        {
          value: ProvisioningState.ACTIVE,
          text: `${i18n.t("PROVISIONED")} (${i18n.t("ACTIVE")})`,
          icon: <PolyIcon icon="server_active" className="v-middle" />
        },
        {
          value: ProvisioningState.STAGED,
          text: i18n.t("STAGED"),
          icon: <PolyIcon icon="server_staged" className="v-middle" />
        }
      ];

      if (filterState.provisioningState != null) {
        provisionedOptions.push({
          value: "",
          text: i18n.t("CLEAR_FILTER")
        });
      }

      return (
        <th
          className="provisioning-status fixed-width text-center"
          key={column}
        >
          <span className="icon-container">
            <DropdownList
              name="provisioned-status"
              className="provisioned-table-filter"
              placeholder={i18n.t("STATE")}
              options={provisionedOptions}
              onChange={value =>
                updateFilter({
                  ...filterState,
                  provisioningState:
                    value !== "" &&
                    Object.values(ProvisioningState).includes(
                      value as ProvisioningState
                    )
                      ? (value as ProvisioningState)
                      : undefined
                })
              }
              value={filterState.provisioningState}
              inputReplacement={
                filterState.provisioningState ? (
                  <PolyIcon
                    icon={
                      getProvisioningConnectionInfo(
                        filterState.provisioningState
                      ).provConnectionIcon
                    }
                  />
                ) : null
              }
            />
          </span>
        </th>
      );
    case ALL_COLUMNS.LINES:
    case ALL_COLUMNS.POLY_PLUS:
    case ALL_COLUMNS.LINKED_DEVICES:
      return <TableHeaderItem fieldName={column} title={title} key={column} />;
    default:
      const field = Object.keys(mapFieldToGraphQL).find(
        key => mapFieldToGraphQL[key] === column
      );
      const options = filterOptions[column];
      const filter =
        field && options
          ? {
              filterValue: (filterState[field as keyof DeviceFilterState] ??
                "") as string,
              filterOptions: options,
              onFilter: (_: string, value: string) =>
                updateFilter({
                  ...filterState,
                  [field]: value !== "" ? value : undefined
                })
            }
          : undefined;

      const sort = filterState.sort;
      return (
        <TableHeaderItem
          fieldName={column}
          title={title}
          sort={{
            currentSort: sort
              ? {
                  field: mapFieldToGraphQL[sort.field],
                  direction: sort.direction
                }
              : {
                  field: COLUMNS.NAME,
                  direction: "ASC"
                },
            onSort: (value: SortState) =>
              updateFilter({
                ...filterState,
                sort: {
                  field: field ?? value.field,
                  direction: value.direction
                }
              })
          }}
          filter={filter}
          key={column}
        />
      );
  }
};

export const LinkedDevicesList: React.FC<{
  entityId: string;
}> = ({ entityId }) => {
  const { data, fetching } = useLinkedDevicesForDeviceQuery({
    variables: {
      id: entityId
    }
  });

  const entityConnections = useEntityConnections(
    data?.device?.entityConnections
  );
  if (fetching) {
    return <LoadingIndicator className="inline" />;
  }

  return (
    <ul
      className={"mv-0 pl-0 linked-device-list"}
      data-testid={"linked-devices-list-popup"}
    >
      {entityConnections
        ?.sort((a, b) => Number(b.connected) - Number(a.connected))
        ?.map((ec, i) => {
          return (
            <div key={ec.targetEntity.name}>
              {!ec.connected &&
                i !== 0 &&
                entityConnections[i - 1].connected !== false && (
                  <li className="divider d-block" />
                )}
              <li className={"ph-15 pv-5 mv-14 text-left font-sm d-block"}>
                {ec.targetEntity.connected !== null && (
                  <ConnectionStatusIcon
                    connected={ec.targetEntity.connected}
                    showDotIcon={true}
                    className={ec.connected ? "" : "disabled"}
                    data-testid={"target-entity-linked-device-icon"}
                  />
                )}
                <ConnectionStatusIcon
                  className={`pl-15 pr-1${
                    ec.connected ? "" : " disabled"
                  } linked-device-status-indicator`}
                  connected={!!ec.connected}
                  showDotIcon={false}
                  data-testid={"entity-linked-device-icon"}
                />

                <Link
                  className={"text-default"}
                  to={`/manage/inventory/details/${ec.targetEntity.id}`}
                  data-testid={ec.targetEntity.name}
                >
                  {ec.targetEntity.name !== null
                    ? ec.targetEntity.name
                    : "Unknown"}
                </Link>
              </li>
            </div>
          );
        })}
    </ul>
  );
};

export const getTableContent = (
  column: string,
  COLUMNS: Record<string, any>,
  device: DeviceRowProps,
  setSubTableOpen?: (
    value: ((prevState: boolean) => boolean) | boolean
  ) => void,
  policyView?: boolean
) => {
  let content: ReactNode = null;

  const {
    accountProvisioningDisabled,
    activeApplicationName,
    connectionStatus,
    dateRegistered,
    entityConnections,
    hardwareModel,
    id,
    internalIp,
    lastConfigRequestDate,
    lastDetected,
    macAddress,
    name,
    room,
    serialNumber,
    site,
    softwareBuild,
    softwareVersion,
    supportsSettings,
    proxyAgent,
    supportsSoftwareUpdate,
    provisioningEnabled,
    linkedDevicesTooltipHidden,
    hideLinkedDevicesTooltip
  } = device;
  const isDesktopDevice = proxyAgent === DESKTOP_PROXY_AGENT;
  const deviceHasOptions = deviceHasExtraOptions(
    supportsSettings,
    supportsSoftwareUpdate,
    provisioningEnabled
  );

  const provisioningState = getProvisioningState(lastConfigRequestDate);
  const isProvisioned = isProvisionedDevice(
    hardwareModel,
    lastConfigRequestDate
  );
  const showProvisioningIcon = isProvisioned && !accountProvisioningDisabled;

  const statusString = getStatusString(!!connectionStatus);

  const {
    provConnectionString,
    provConnectionClass,
    provConnectionIcon
  } = getProvisioningConnectionInfo(provisioningState);
  switch (column) {
    case COLUMNS.CONNECTION:
      return (
        <ConnectionStatusCell key={column}>
          {connectionStatus !== null && (
            <ToolTip
              iconTitle={i18n.t("STATUS_INDICATORS")}
              position="bottom"
              size="sm"
              iconReplacement={
                <ConnectionStatusIcon
                  connected={connectionStatus}
                  showDotIcon
                  className="m-0"
                />
              }
            >
              <span>{statusString}</span>
            </ToolTip>
          )}
        </ConnectionStatusCell>
      );
    case COLUMNS.PROVISIONING:
      return (
        <td
          className="provisioning-status fixed-width text-center"
          key={column}
        >
          <span className="icon-container">
            {showProvisioningIcon && (
              <ToolTip
                iconTitle={i18n.t("STATUS_INDICATORS")}
                position="bottom"
                size="xs"
                iconReplacement={
                  <span
                    data-testid="server-icon"
                    className={provConnectionClass}
                  >
                    <PolyIcon icon={provConnectionIcon} title="" />
                  </span>
                }
              >
                <span>{provConnectionString}</span>
              </ToolTip>
            )}
          </span>
        </td>
      );
    case COLUMNS.LINKED_DEVICES:
      const linkedDevices = entityConnections?.edges;
      const linkedDeviceStatus = getLinkedDevicesStatus(
        linkedDevices?.map(edge => edge.node)
      );
      return (
        <td
          className="text-center linked-device-status"
          key={column}
          data-testid={"linked-device"}
        >
          <span
            className="icon-container"
            onClick={() => hideLinkedDevicesTooltip!(false)}
          >
            {linkedDevices != null && linkedDevices.length > 0 && (
              <ToolTip
                hideTooltipOnClick={hideLinkedDevicesTooltip!}
                iconTitle={i18n.t("LINKED_DEVICES")}
                position="bottom"
                size="dynamic"
                disablePopup={linkedDevicesTooltipHidden}
                testDataId={"linked-devices"}
                iconReplacement={
                  <LinkedDeviceIcon
                    hasLinkedDevices={linkedDevices.length > 0}
                    allDevicesAreOnline={linkedDeviceStatus || false}
                  />
                }
              >
                <LinkedDevicesList entityId={linkedDevices[0]?.node.entityId} />
              </ToolTip>
            )}
          </span>
        </td>
      );
    case COLUMNS.NAME:
      if (policyView) {
        if (isDesktopDevice || provisioningEnabled) {
          content = (
            <Link
              persist={["PolicyFilterState", "device", "deviceModel"]}
              to={{
                pathname: `/manage/policy/details/settings`,
                search: `deviceModel=${hardwareModel ??
                  name}&device=${id}&previous=device`
              }}
            >
              {handleNullFields(name)}
            </Link>
          );
        } else {
          content = (
            <Link
              to={`/manage/inventory/details/${id}${
                !!connectionStatus || showProvisioningIcon ? "/settings" : ""
              }`}
            >
              {handleNullFields(name)}
            </Link>
          );
        }
      } else {
        content = (
          <Link to={`/manage/inventory/details/${id}`}>
            {handleNullFields(name)}
          </Link>
        );
      }
      break;
    case COLUMNS.SERIAL_NUMBER:
      content = handleNullFields(serialNumber);
      break;
    case COLUMNS.MAC_ADDRESS:
      content = handleNullFields(macAddress);
      break;
    case COLUMNS.IPV4:
      content = handleNullFields(internalIp);
      break;
    case COLUMNS.ROOM:
      content = handleNullFields(room?.name ?? "");
      break;
    case COLUMNS.SITE:
      content = handleNullFields(site?.name);
      break;
    case COLUMNS.MODEL:
      content = handleNullFields(hardwareModel);
      break;
    case COLUMNS.VERSION:
      content = handleNullFields(
        getSoftwareVersion(softwareVersion, softwareBuild, hardwareModel)
      );
      break;
    case COLUMNS.LINES:
      if (!setSubTableOpen) {
        content = "";
        console.warn(
          "Lines column included in table, but setSubTableOpen argument not provided."
        );
      } else {
        content = <ExpandCellVisibility id={id} onChange={setSubTableOpen} />;
      }
      break;
    case COLUMNS.PROVIDER:
      content = handleNullFields(activeApplicationName, true);
      break;
    case COLUMNS.LAST_PROVISIONING_SYNC:
      content =
        deviceHasOptions && !isDesktopDevice
          ? provisioningState === ProvisioningState.ACTIVE ||
            provisioningState === ProvisioningState.INACTIVE
            ? formatDate(lastConfigRequestDate)
            : handleNullFields(null, true)
          : "-";
      break;
    case COLUMNS.DATE_REGISTERED:
      content =
        deviceHasOptions && !isDesktopDevice
          ? dateRegistered
            ? formatDate(dateRegistered)
            : i18n.t("REGISTER_DEVICE_PRIOR")
          : "-";
      break;
    case COLUMNS.DATE_LAST_SEEN:
      content =
        deviceHasOptions && !isDesktopDevice
          ? lastDetected
            ? formatDate(lastDetected, "P")
            : handleNullFields(lastDetected, true)
          : "-";
      break;
    case COLUMNS.POLY_PLUS:
      content = serialNumber ? (
        <PolyPlusStatusCell serialNumber={serialNumber} />
      ) : null;
      break;
  }

  return <td key={column}>{content}</td>;
};

export const LinkedDeviceIcon: React.FC<{
  onClick?: () => void;
  hasLinkedDevices: boolean;
  allDevicesAreOnline: boolean;
  className?: string;
  "data-testid"?: string;
}> = ({
  onClick,
  hasLinkedDevices,
  allDevicesAreOnline = true,
  className,
  ...rest
}) => {
  const icon = hasLinkedDevices
    ? allDevicesAreOnline
      ? "linked_yes"
      : "linked_no"
    : "";
  return (
    <span
      data-testid={rest["data-testid"]}
      className="linked-device-status-indicator"
    >
      <PolyIcon
        title=""
        onClick={onClick}
        icon={icon}
        data-testid={"linked-device-icon"}
        className={`status-indicator ${className ?? ""}`}
      />
    </span>
  );
};

export const DeviceRow: React.FC<DeviceRowProps & {
  table?: keyof typeof TABLES;
}> = props => {
  const {
    checked,
    count,
    hardwareModel,
    id,
    index,
    listLength,
    onCheck,
    setCursor,
    shownColumns,
    showChecks,
    table = "DEVICES",
    policyView
  } = props;
  const [tooltipHidden, setTooltipHidden] = useState(true);
  const [visible, setVisible] = useState(false);
  const [subTableOpen, setSubTableOpen] = useState(false);

  const onChange = (isVisible: boolean) => {
    if (isVisible) {
      setVisible(isVisible);
      if (index === listLength - 1 && listLength < count) {
        setCursor(c => c + 100);
      }
    }
  };

  const toggleToolTip = (hideToolTip: boolean) => {
    setTooltipHidden(hideToolTip);
  };

  const { COLUMNS, HIDDEN } = TABLES[table];

  const isPolyOs = isPolyOS(hardwareModel || "");

  const userCanDelete = useGuard(needs("unregister:device"));

  return (
    <VisibilitySensor
      offset={{ bottom: -300 }}
      active={!visible}
      onChange={onChange}
    >
      <>
        <TableRow className="disabled" checked={checked}>
          {userCanDelete && showChecks && (
            <CheckTableData onCheck={() => onCheck(id)} checked={checked} />
          )}
          {shownColumns.map(column =>
            getTableContent(
              column,
              { ...COLUMNS, ...HIDDEN },
              {
                hideLinkedDevicesTooltip: toggleToolTip,
                linkedDevicesTooltipHidden: tooltipHidden,
                ...props
              },
              setSubTableOpen,
              policyView
            )
          )}
        </TableRow>
        {subTableOpen && <NestedLinesTable deviceId={id} isPolyOs={isPolyOs} />}
      </>
    </VisibilitySensor>
  );
};

export const getDeviceColumnTranslations = () => {
  const { COLUMNS, HIDDEN } = TABLES.DEVICES;
  const ALL_COLUMNS = { ...COLUMNS, ...HIDDEN };

  return {
    [ALL_COLUMNS.NAME]: i18n.t("DEVICE_NAME"),
    [ALL_COLUMNS.SERIAL_NUMBER]: i18n.t("SERIAL_NUMBER"),
    [ALL_COLUMNS.LINES]: i18n.t("LINES"),
    [ALL_COLUMNS.MAC_ADDRESS]: i18n.t("MAC_ADDRESS"),
    [ALL_COLUMNS.IPV4]: i18n.t("IPV4"),
    [ALL_COLUMNS.ROOM]: i18n.t("ROOM"),
    [ALL_COLUMNS.SITE]: i18n.t("SITE"),
    [ALL_COLUMNS.MODEL]: i18n.t("DEVICE_MODEL"),
    [ALL_COLUMNS.VERSION]: i18n.t("VERSION"),
    [ALL_COLUMNS.PROVIDER]: i18n.t("PROVIDER"),
    [ALL_COLUMNS.CONNECTION]: i18n.t("STATUS"),
    [ALL_COLUMNS.LINKED_DEVICES]: i18n.t("LINKED_DEVICES"),
    [ALL_COLUMNS.PROVISIONING]: i18n.t("STATE"),
    [ALL_COLUMNS.LAST_PROVISIONING_SYNC]: i18n.t("LAST_PROVISIONING_TOUCH"),
    [ALL_COLUMNS.DATE_REGISTERED]: i18n.t("DEVICE_ADDED"),
    [ALL_COLUMNS.DATE_LAST_SEEN]: i18n.t("DEVICE_LAST_SEEN"),
    [ALL_COLUMNS.POLY_PLUS]: i18n.t("POLY_PLUS")
  };
};

const DefaultDeviceTableViewLoader: React.FC = () => {
  const [searchTerm] = useSearchTerm();
  const [cursor, setCursor] = useState(0);
  const dispatch = useDispatch();
  const geofilter = useSelector((state: State) => state.devicesMap.geofilter);
  const { path } = useRouteMatch();

  const simpleTableView = path.includes("policy");
  const { data, fetching, stale, executor } = useDevices(
    geofilter,
    searchTerm ?? undefined,
    {},
    cursor
  );

  // total device counts without any filters applied
  // need this count to decide whether to show empty state for devices
  const { count: totalCount, fetching: countFetching } = useDeviceCount();

  const doClearGeofilter = () => {
    dispatch(clearGeofilter());
  };

  let devices: Array<DeviceCardProps> = [];
  let aggregates: DevicesLocationQuery["deviceAggregations"] = [];
  let count = 0;
  if (data && data.deviceList) {
    devices = data.deviceList;
    count = data.deviceCount;
    aggregates = data.deviceAggregations;
  }

  return (
    <DefaultDeviceTableView
      setCursor={setCursor}
      devices={devices}
      aggregates={aggregates}
      loading={fetching || countFetching}
      refreshDevices={executor}
      stale={stale}
      count={count}
      totalCount={totalCount}
      geofilter={geofilter}
      clearGeofilterFcn={doClearGeofilter}
      simpleTableView={simpleTableView}
    />
  );
};

export const ConnectionStatusCell: React.FC<{ header?: boolean }> = ({
  header,
  children
}) => {
  const Wrapper = header ? "th" : "td";

  return (
    <Wrapper className="connection-status fixed-width text-center">
      <span className="icon-container">{children}</span>
    </Wrapper>
  );
};

export const ConnectionStatusHeader = <
  T extends DeviceFilterState | Record<string, string>
>({
  fieldName,
  filterState,
  setFilter
}: ConnectionStatusProps<T>) => {
  const { t } = useTranslation();

  const trueOption = {
    value: fieldName === "status" ? "online" : "true",
    text: t("ONLINE")
  };
  const falseOption = {
    value: fieldName === "status" ? "offline" : "false",
    text: t("OFFLINE")
  };

  const classes = "m-0";
  const options: Array<OptionType> = [
    {
      ...trueOption,
      icon: <ConnectionStatusIcon connected={true} className={classes} />
    },
    {
      ...falseOption,
      icon: <ConnectionStatusIcon connected={false} className={classes} />
    }
  ];

  if (filterState[fieldName] != null) {
    options.push({
      value: "",
      text: t("CLEAR_FILTER")
    });
  }

  return (
    <ConnectionStatusCell header>
      <DropdownList
        name="connection"
        className="connection-table-filter"
        placeholder={t("STATUS")}
        options={options}
        onChange={value =>
          setFilter({
            ...filterState,
            [fieldName]: value !== "" ? value : undefined
          })
        }
        value={filterState[fieldName]}
        inputReplacement={
          filterState[fieldName] ? (
            <ConnectionStatusIcon
              connected={filterState[fieldName] === trueOption.value}
              className={classes}
            />
          ) : null
        }
      />
    </ConnectionStatusCell>
  );
};

const PolyPlusStatusCell: React.FC<{
  serialNumber: string;
}> = ({ serialNumber }) => {
  const { tenant } = useContext(SelectedTenantContext);
  const { data, fetching } = useActiveEntitlementsBySerialQuery({
    variables: {
      tenantId: tenant?.id ?? "",
      productSerial: serialNumber
    },
    pause: !tenant
  });

  if (fetching) return <PolyIcon icon="refresh" className="spin" />;
  if (data?.entitlements.edges.length === 0) return null;

  return <span data-testid="poly-plus-status">&#10003;</span>;
};

const GroupDevicesButton: React.FC<{
  disabled: boolean;
}> = ({ disabled }) => {
  const { t } = useTranslation();
  return (
    <DropdownButton
      className="btn-secondary"
      title={t("GROUP")}
      disabled={disabled}
    >
      <Link to={infiniteNestLink("/create-group")}>
        {t("CREATE_NEW_GROUP")}
      </Link>
      <Link to={infiniteNestLink("/add-to-group")}>
        {t("ADD_TO_EXISTING_GROUP")}
      </Link>
    </DropdownButton>
  );
};

export const DefaultDeviceTableView: React.FC<{
  setCursor: Dispatch<SetStateAction<number>>;
  refreshDevices: (opts?: Partial<OperationContext> | undefined) => void;
  devices: Array<DeviceCardProps>;
  aggregates: DevicesLocationQuery["deviceAggregations"];
  loading: boolean;
  stale: boolean;
  count: number;
  totalCount: number;
  geofilter: boolean;
  clearGeofilterFcn: FilterActionFcn;
  simpleTableView: boolean;
}> = ({
  setCursor,
  devices,
  aggregates,
  loading,
  stale,
  count,
  totalCount,
  geofilter,
  clearGeofilterFcn,
  refreshDevices,
  simpleTableView
}) => {
  const { t } = useTranslation();
  const verifyAndFormat = verifyAndFormatAggregates(aggregates);
  const { tenant } = useContext(SelectedTenantContext);

  const [selectedItems, setSelectedItems] = useState<Array<string>>([]);

  const selectedDevices = devices
    .filter(device => selectedItems.includes(device.id))
    .map(device => ({
      id: device.id,
      serial: device.serialNumber,
      name: device.name,
      hardwareModel: device.hardwareModel,
      lastConfigRequestDate: device.lastConfigRequestDate,
      connected: device.connected
    }));

  const userCanAdd = useGuard(needs("register:device"));
  const userCanDelete = useGuard(needs("unregister:device"));

  const refresh = () => {
    setSelectedItems([]);
    refreshDevices({ requestPolicy: "network-only" });
  };

  useEffect(() => {
    setSelectedItems([]);
  }, [tenant?.id]);

  const { NAME: TABLE_NAME, COLUMNS } = TABLES.DEVICES;
  const {
    [TABLE_COLUMN_STATE.SHOWN]: shownColumns,
    [TABLE_COLUMN_STATE.HIDDEN]: hiddenColumns
  } = useSelector((state: State) => {
    return state.userMeta?.tables[TABLE_NAME] ?? {};
  });
  const [filterState, setFilter, resetFilters] = useDeviceFilterValue();
  const [pageLoading, setPageLoading] = useState(false);
  const getValue = (bucket: { value: string; count: number }) => bucket.value;
  const filterOptions = {
    [COLUMNS.MODEL]: verifyAndFormat("hardwareModel").map(getValue),
    [COLUMNS.SITE]: verifyAndFormat("site").map(getValue),
    [COLUMNS.VERSION]: verifyAndFormat("softwareVersion").map(getValue)
  };

  const fetching = loading || stale;
  useEffect(() => {
    if (!fetching) {
      setPageLoading(false);
    }
  }, [fetching]);

  const { data: provData } = useProvisioningAccountsQuery({
    variables: { tenantUuid: tenant?.id },
    context: noOpNotifyContext
  });

  const account = provData?.getProvisioningAccounts?.[0];
  const provDisabled = !account || !!account.isDisabled;

  const columnTranslations = getDeviceColumnTranslations();

  const updateCursor: Dispatch<SetStateAction<number>> = cursor => {
    setPageLoading(true);
    setCursor(cursor);
  };

  const updateFilter = (newFilterState: DeviceFilterState) => {
    setCursor(0);
    setFilter(newFilterState);
  };

  const shown = getColumns(shownColumns, columnTranslations);
  const hidden = getColumns(hiddenColumns, columnTranslations);

  const clearGeofilterVisibility = geofilter ? "visible" : "";
  const { Content } = Layout;

  // checkbox selection
  const toggleItem = toggleItemWithSet(
    devices.length,
    selectedItems,
    setSelectedItems
  );
  const itemChecked = itemCheckedWithSet(selectedItems);

  const mapToDeviceIn = (devices: Array<DeviceInfo>): Array<DeviceIn> =>
    devices
      .filter(device => device.serial)
      .map(device => ({
        serial: device.serial!,
        deviceModel: device.hardwareModel ?? ""
      }));

  const devicesToGroup = selectedDevices.filter(
    ({ serial, hardwareModel, lastConfigRequestDate }) =>
      !!serial && isProvisionedDevice(hardwareModel, lastConfigRequestDate)
  );
  const showGroupNote = devicesToGroup.length !== selectedDevices.length;

  // Dial Out options is only available when single, online UCS device is selected
  const dialOutAvailable =
    selectedDevices.length === 1 &&
    isUcsDevice(selectedDevices[0].hardwareModel ?? "") &&
    !!selectedDevices[0].connected;

  if (simpleTableView) {
    return (
      <>
        <div className="table-actions mb-15">
          <TableSearchEdit
            search={{
              onSearch: term => {
                if (term !== undefined) {
                  setCursor(0);
                }
              }
            }}
            edit={{
              tableName: TABLE_NAME,
              shownColumns: shown,
              hiddenColumns: hidden
            }}
          />
        </div>
        <div className="table-container" data-testid="simple-table-view">
          <table className="table custom-table table-filterable">
            <TableHeader>
              {generateTableHeaders(
                shown,
                filterState,
                updateFilter,
                filterOptions
              )}
              <ClearFiltersHeader
                showClearFilters={areFiltersSelected(filterState)}
                onClearFilters={resetFilters}
              />
            </TableHeader>
            <TableBody
              loading={fetching && !pageLoading}
              cols={shown.length}
              accommodateScrollbar
            >
              {devices.map((device, index) => (
                <DeviceRow
                  {...device}
                  key={device.id}
                  accountProvisioningDisabled={provDisabled}
                  checked={itemChecked(device.id)}
                  connectionStatus={device?.connected}
                  count={count}
                  index={index}
                  listLength={devices.length}
                  onCheck={() => toggleItem(device.id)}
                  setCursor={updateCursor}
                  showChecks={false}
                  shownColumns={shown.map(column => column.key)}
                  policyView={true}
                />
              ))}
            </TableBody>
          </table>
          {!fetching && !devices.length ? (
            <NoResultsIndicator title={t("NO_DEVICES")} />
          ) : null}
          {fetching && pageLoading ? (
            <LoadingIndicator
              key="loading"
              className={devices.length ? "inline" : ""}
            />
          ) : null}
        </div>
      </>
    );
  }

  return (
    <Layout viewSelector={<ViewSelector />}>
      <Content className="device-inventory no-sidebar" data-testid="table-view">
        {!fetching && totalCount === 0 ? (
          <NoDevicesIndicator />
        ) : (
          <>
            <div className="table-heading" data-testid="metric-heading">
              <span className="metric">{count}</span>
              <span className="title ml-5">{t("DEVICES")}</span>
              <span
                onClick={clearGeofilterFcn}
                className={`clear-geofilter ${clearGeofilterVisibility}`}
                role="button"
              >
                <PolyIcon icon="clear_x" />
                {t("CLEAR_GEOFILTER")}
              </span>
            </div>
            <div className="table-actions mb-15 d-flex space-between">
              <div>
                {userCanAdd && (
                  <Link to="/manage/register" className="btn btn-primary">
                    {t("ADD_DEVICE")}
                  </Link>
                )}
                <Guard requirement={"UPDATE_SITES"}>
                  <div className="inline-block mr-5 ml-5">
                    {showGroupNote ? (
                      <ToolTip
                        size="md"
                        className="text-md"
                        position="bottom"
                        iconTitle="info"
                        iconReplacement={
                          <GroupDevicesButton
                            disabled={devicesToGroup.length === 0}
                          />
                        }
                      >
                        {devicesToGroup.length === 0
                          ? t("DEVICE_GROUP_ONLY_PROVISIONED_ERROR")
                          : t("DEVICE_GROUP_ONLY_PROVISIONED_WARNING")}
                      </ToolTip>
                    ) : (
                      <GroupDevicesButton
                        disabled={devicesToGroup.length === 0}
                      />
                    )}
                  </div>
                </Guard>
                {selectedItems.length > 0 ? (
                  <ProtectedLink
                    to={infiniteNestLink("/deleteDevices")}
                    className="btn"
                    requirement="unregister:device"
                  >
                    {t("DELETE_DEVICE", {
                      count: selectedItems.filter(item => item !== HEADER_ID)
                        .length
                    })}
                  </ProtectedLink>
                ) : (
                  <ProtectedLink
                    to={""}
                    className="btn disabled"
                    requirement="unregister:device"
                  >
                    {t("DELETE_DEVICE")}
                  </ProtectedLink>
                )}
                {
                  <FeatureFlagEnabled
                    feature="CALL_CONTROL"
                    andIf={dialOutAvailable}
                  >
                    <Link to={infiniteNestLink("/dialOut")} className="btn">
                      {t("DIAL_OUT")}
                    </Link>
                  </FeatureFlagEnabled>
                }
              </div>
              <div>
                {<DeviceInventoryExport />}
                <TableSearchEdit
                  search={{
                    onSearch: term => {
                      if (term !== undefined) {
                        setCursor(0);
                      }
                    }
                  }}
                  edit={{
                    tableName: TABLE_NAME,
                    shownColumns: shown,
                    hiddenColumns: hidden
                  }}
                />
              </div>
            </div>
            <div className="table-container">
              <table className="table custom-table table-filterable">
                <TableHeader>
                  {userCanDelete && !!shownColumns.length && (
                    <CheckTableHeader
                      checked={itemChecked(HEADER_ID)}
                      onCheck={() =>
                        toggleAll(
                          devices.map(device => device.id),
                          selectedItems,
                          setSelectedItems
                        )
                      }
                    />
                  )}
                  {generateTableHeaders(
                    shown,
                    filterState,
                    updateFilter,
                    filterOptions
                  )}
                  <ClearFiltersHeader
                    showClearFilters={areFiltersSelected(filterState)}
                    onClearFilters={resetFilters}
                  />
                </TableHeader>
                <TableBody
                  loading={fetching && !pageLoading}
                  cols={shown.length}
                  accommodateScrollbar
                >
                  {devices.map((device, index) => (
                    <DeviceRow
                      {...device}
                      key={device.id}
                      accountProvisioningDisabled={provDisabled}
                      checked={itemChecked(device.id)}
                      connectionStatus={device.connected}
                      count={count}
                      index={index}
                      listLength={devices.length}
                      onCheck={() => toggleItem(device.id)}
                      setCursor={updateCursor}
                      showChecks={userCanDelete && !!shownColumns.length}
                      shownColumns={shown.map(column => column.key)}
                      policyView={false}
                    />
                  ))}
                </TableBody>
              </table>
              {!fetching && !devices.length ? (
                <NoResultsIndicator title={t("NO_DEVICES")} />
              ) : null}
              {fetching && pageLoading ? (
                <LoadingIndicator
                  key="loading"
                  className={devices.length ? "inline" : ""}
                />
              ) : null}
            </div>
          </>
        )}
      </Content>
      <ProtectedRoute
        nest
        path="/deleteDevices"
        requirement="unregister:device"
      >
        <DeleteBulkDevicesModal devices={selectedDevices} refresh={refresh} />
      </ProtectedRoute>
      <ProtectedRoute nest path="/create-group" requirement={"UPDATE_SITES"}>
        <CreateDeviceGroupModal
          devices={mapToDeviceIn(devicesToGroup)}
          onSuccess={() => setSelectedItems([])}
        />
      </ProtectedRoute>
      <ProtectedRoute nest path="/add-to-group" requirement={"UPDATE_SITES"}>
        <AddToExistingGroupModal
          devices={mapToDeviceIn(devicesToGroup)}
          onSuccess={() => setSelectedItems([])}
        />
      </ProtectedRoute>
      <FeatureFlagEnabled feature="CALL_CONTROL" andIf={dialOutAvailable}>
        <Route nest path="/dialOut">
          <DialOutModal deviceId={selectedDevices[0]?.id} />
        </Route>
      </FeatureFlagEnabled>
    </Layout>
  );
};

export default DefaultDeviceTableViewLoader;
